import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Table } from '@tanstack/react-table';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { NavigableItem } from 'services/schemableFormNavigation/schemableFormNavigation';
import { EMPTY_FIELD } from 'support/helpers/const/const';

import { generateEmbeddedActionTestId } from '../../../utils/generateEmbeddedActionTestId';

type Props<TData> = {
  row: Row<TData>;
  table: Table<TData>;
  /** Real number of items in the collapsed array, which the row itself no longer shows. */
  itemsCount: number;
  parentColumnPath: string;
};

/**
 * Stands in for a collapsed column group: one cell spanning the group's sub-columns, saying how
 * many items are hidden and how many of them carry an annotation. The group header above already
 * names what the items are, so the summary counts them rather than repeating the label.
 *
 * The annotation count matters because collapsing hides fields from view but never from
 * validation — a datacheck on a hidden item still blocks submission, so it has to stay visible
 * here.
 */
export const CollapsedEmbeddedGroupCell = <TData,>({ row, table, itemsCount, parentColumnPath }: Props<TData>) => {
  const { t } = useTranslation();
  const { service } = useNavigationContext<NavigableItem>();
  // `useActiveItems` drops acknowledged and pending-validation items, which is what the banner
  // counters use — a badge counting the raw store would call an issue unresolved that the rest
  // of the page shows as resolved.
  const annotations = service.useActiveItems();
  const tableMeta = table.options.meta;

  const testId = useMemo(
    () =>
      generateEmbeddedActionTestId({
        dataPrefix: tableMeta?.dataPrefix,
        rowIndex: row.index,
        parentColumnPath,
        suffix: 'collapsedEmbeddedGroupCell',
      }),
    [tableMeta?.dataPrefix, row.index, parentColumnPath],
  );

  const annotationCount = useMemo(() => {
    const prefix = `${tableMeta?.dataPrefix ? `${tableMeta.dataPrefix}.` : ''}${row.index}.${parentColumnPath}.`;
    return annotations.filter((annotation) => annotation.fieldPath.startsWith(prefix)).length;
  }, [annotations, tableMeta?.dataPrefix, row.index, parentColumnPath]);

  if (!itemsCount) {
    return (
      <div className="flex size-full items-center px-2 text-gray-400" data-testid={testId}>
        {EMPTY_FIELD}
      </div>
    );
  }

  return (
    <div className="flex size-full items-center gap-2 px-2 text-gray-500" data-testid={testId}>
      <span>{t('webedi:embeddedRows.collapsedSummary', { count: itemsCount })}</span>
      {annotationCount > 0 && (
        <span className="rounded-sm bg-red-50 px-1.5 py-0.5 text-red-700">
          {t('webedi:embeddedRows.collapsedAnnotations', { count: annotationCount })}
        </span>
      )}
    </div>
  );
};
